/**
 * The single way this app talks to the API. Every endpoint requires a bearer token, so there is no
 * such thing as an unauthenticated request here — including downloads, which is why they cannot be
 * plain anchors (see download.ts).
 */

/**
 * One thing found wrong with a document, as the API states it: a business rule it broke (the code is
 * the rule's own id, e.g. BR-02 or PEPPOL-EN16931-R010, and `parameters.path` says where in the
 * invoice) or a schema fault (`parameters.line` and `parameters.position` say where in the file).
 */
export interface ApiValidationFailure {
  code: string;
  errorMessage?: string | null;
  parameters?: Record<string, unknown>;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Extension the API sets on refusals a client should distinguish, e.g. Forbidden.TenantNotRegistered. */
  errorCode?: string;
  /** Extension carrying the structured detail of a refusal; a rejected document lists its faults here. */
  messageParams?: { failures?: ApiValidationFailure[]; [key: string]: unknown };
  /** Validation failures, keyed by member name. */
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, message?: string) {
    super(message ?? problem?.detail ?? problem?.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  get errorCode(): string | undefined {
    return typeof this.problem?.errorCode === 'string' ? this.problem.errorCode : undefined;
  }
}

export const errorCodes = {
  tenantNotRegistered: 'Forbidden.TenantNotRegistered',
  tenantDisabled: 'Forbidden.TenantDisabled',
  roleNotAssigned: 'Forbidden.RoleNotAssigned',
  /** No tenant chosen while the endpoint needs one; render as a state, not as an error. */
  tenantNotSelected: 'Conflict.TenantNotSelected',
  /** The caller reaches no tenant at all — only an administrator's assignment helps. */
  noTenantAssigned: 'Forbidden.NoTenantAssigned',
  /** A disabled tenant must be provisioned to the current schema before it can be enabled. */
  tenantSchemaNotReady: 'Conflict.TenantSchemaNotReady',
  /** The named tenant is not the caller's — typically a stored selection that was revoked. */
  tenantNotSelectable: 'BadRequest.TenantNotSelectable',
  /** The document is not the format it claims to be. */
  schemaValidation: 'BadRequest.SchemaValidation',
  /** The document is sound as a file but states an invoice the rules of its format reject. */
  businessValidation: 'BadRequest.BusinessValidation',
  /** The tenant row exists, but its database schema could not be provisioned. */
  tenantSchemaProvisioning: 'Integration.TenantSchemaProvisioning',
  /** A mapping's target is not among the codes of the active code list for its kind. */
  codeNotInCodelist: 'BadRequest.CodeNotInCodelist',
} as const;

/**
 * The faults a refusal listed, or an empty list when it listed none. Worth rendering in full: the
 * point of the API listing them all at once is that a document gets corrected in one round.
 */
export function getValidationFailures(error: unknown): ApiValidationFailure[] {
  if (!(error instanceof ApiError)) return [];

  const failures = error.problem?.messageParams?.failures;
  return Array.isArray(failures) ? failures : [];
}

export type QueryValue = string | number | boolean | Date | null | undefined;
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;

export interface AccessTokenRequest {
  forceRefresh?: boolean;
}

export type AccessTokenProvider = (request: AccessTokenRequest) => Promise<string>;

let accessTokenProvider: AccessTokenProvider = () =>
  Promise.reject(new Error('No access token provider configured.'));

let unauthorizedHandler: () => void = () => {};

export function setAccessTokenProvider(provider: AccessTokenProvider): void {
  accessTokenProvider = provider;
}

/** Invoked when a request is still unauthorized after one forced token refresh. */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

/** Invoked after every request the server accepted. */
let requestSucceededHandler: () => void = () => {};

export function setRequestSucceededHandler(handler: () => void): void {
  requestSucceededHandler = handler;
}

export const tenantHeaderName = 'AlgoBridge-Tenant';

export type TenantProvider = () => string | null;

let tenantProvider: TenantProvider = () => null;

/** Supplies the globally selected tenant; installed by the provider that owns the choice. */
export function setTenantProvider(provider: TenantProvider): void {
  tenantProvider = provider;
}

let tenantRejectedHandler: () => void = () => {};

/** Invoked when the server refuses the named tenant, so the stale selection can be dropped. */
export function setTenantRejectedHandler(handler: () => void): void {
  tenantRejectedHandler = handler;
}

export const apiBasePath = '/api';

function appendQueryValue(target: URLSearchParams, key: string, value: QueryValue): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string' && value.length === 0) return;

  target.append(key, value instanceof Date ? value.toISOString() : String(value));
}

export function buildQueryString(params: QueryParams | undefined): string {
  if (!params) return '';

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      // ASP.NET Core binds a List<T> from a repeated key, not from a comma-joined value.
      for (const item of value) appendQueryValue(search, key, item);
    } else {
      appendQueryValue(search, key, value as QueryValue);
    }
  }

  const rendered = search.toString();
  return rendered.length > 0 ? `?${rendered}` : '';
}

export interface RequestOptions {
  method?: string;
  query?: QueryParams;
  /** Sent as JSON unless it is a FormData, which the browser must frame itself. */
  body?: unknown;
  headers?: Record<string, string>;
  accept?: string;
  signal?: AbortSignal;
}

async function parseProblem(response: Response): Promise<ProblemDetails | null> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    const text = await response.text().catch(() => '');
    return text.length > 0 ? { detail: text, status: response.status } : null;
  }

  try {
    const parsed: unknown = await response.json();
    return typeof parsed === 'object' && parsed !== null ? (parsed as ProblemDetails) : null;
  } catch {
    return null;
  }
}

function buildInit(options: RequestOptions, token: string, tenant: string | null): RequestInit {
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (tenant !== null) headers.set(tenantHeaderName, tenant);
  if (!headers.has('Accept')) headers.set('Accept', options.accept ?? 'application/json');

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.signal) init.signal = options.signal;

  if (options.body instanceof FormData) {
    // Setting Content-Type by hand would drop the multipart boundary the browser generates.
    init.body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    init.body = JSON.stringify(options.body);
  }

  return init;
}

/**
 * Issues one authenticated request and returns the raw response, so binary endpoints can stream.
 * A 401 is retried once with a forced token refresh before the caller is sent back to sign in.
 */
export async function apiFetch(path: string, options: RequestOptions = {}): Promise<Response> {
  const url = `${apiBasePath}${path}${buildQueryString(options.query)}`;

  // Captured once per request rather than per attempt: a 401 retry must repeat the same request,
  // not pick up a tenant switched mid-flight and file the response under the old cache key.
  // GET /tenant never names one — it is the road back to a valid selection after one is revoked,
  // and the server refuses a stale header even there.
  const tenant = path === '/tenant' ? null : tenantProvider();

  const send = async (forceRefresh: boolean): Promise<Response> => {
    const token = await accessTokenProvider({ forceRefresh });
    return fetch(url, buildInit(options, token, tenant));
  };

  let response = await send(false);

  if (response.status === 401) {
    response = await send(true);
    if (response.status === 401) {
      unauthorizedHandler();
      throw new ApiError(401, await parseProblem(response));
    }
  }

  if (!response.ok) {
    const problem = await parseProblem(response);
    // Both refusals mean the current tenant context cannot serve the request; the injected provider
    // decides whether there is a stale selection to clear or the unselected state is already correct.
    if (
      (response.status === 400 && problem?.errorCode === errorCodes.tenantNotSelectable) ||
      (response.status === 409 && problem?.errorCode === errorCodes.tenantNotSelected)
    ) {
      tenantRejectedHandler();
    }
    throw new ApiError(response.status, problem);
  }

  requestSucceededHandler();
  return response;
}

export async function apiJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await apiFetch(path, options);
  return (await response.json()) as T;
}

export async function apiVoid(path: string, options: RequestOptions = {}): Promise<void> {
  const response = await apiFetch(path, options);
  // Drain 204s too: an unread body keeps the connection from being reused.
  await response.arrayBuffer().catch(() => undefined);
}

/** Best-effort message for a toast, preferring the ProblemDetails detail the API took care to write. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.problem?.detail ?? error.problem?.title ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
